import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import {
  Users, Crown, ClipboardCheck, MessageSquare, CalendarDays, Trophy, Wallet,
  Check, X, ChevronRight, ArrowRight,
} from "lucide-react";
import { RegattasTab, AgendaTab } from "./AdminExtensions";
import AdminContentTab from "./AdminContentTab";
import { ProductsTab } from "./AdminDashboard";
import StageRegistrationsPanel from "../components/StageRegistrationsPanel";

export default function President() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);

  const load = async () => {
    try {
      const [d, a, m, p] = await Promise.all([
        api.get("/president/dashboard"),
        api.get("/president/admissions"),
        api.get("/president/contact-messages"),
        api.get("/admin/pending-users"),
      ]);
      setData(d.data); setAdmissions(a.data); setMessages(m.data); setPendingUsers(p.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  if (!user || !(["admin", "president"].includes(user.role) || (user.roles || []).some((r) => ["admin", "president"].includes(r)))) return null;

  const TABS = ["overview", "pending", "admissions", "messages", "agenda", "regattas", "events", "stages", "products", "content"];
  const LABEL = {
    overview: "Vue d'ensemble", pending: "Inscriptions à valider", admissions: "Admissions",
    messages: "Messages", agenda: "Agenda",
    regattas: "Gérer les régates", events: "Gérer les événements",
    stages: "Inscriptions stages", products: "Boutique / Stages", content: "Contenu du site",
  };

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="president-page">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="flex items-center gap-3 mb-3">
          <Crown className="w-5 h-5 text-club-yellow-dark" strokeWidth={1.5} />
          <div className="label-eyebrow text-club-red">{tr("Espace Président")}</div>
        </div>
        <h1 className="font-serif text-4xl text-navy">{tr("Direction du club")}</h1>
        <div className="divider-gold mt-5 mb-8" />

        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin" data-testid="president-tabs">
          {TABS.map((k) => (
            <button
              key={k} onClick={() => setTab(k)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 whitespace-nowrap shrink-0 ${
                tab === k ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"
              }`}
              data-testid={`president-tab-${k}`}
            >
              {tr(LABEL[k])}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview data={data} onNav={setTab} />}
        {tab === "pending" && <PendingUsersView users={pendingUsers} reload={load} lang={lang} />}
        {tab === "admissions" && <AdmissionsView admissions={admissions} reload={load} lang={lang} />}
        {tab === "messages" && <MessagesView messages={messages} lang={lang} />}
        {tab === "agenda" && <AgendaView data={data} lang={lang} />}
        {tab === "regattas" && <RegattasTab />}
        {tab === "events" && <AgendaTab />}
        {tab === "stages" && <StageRegistrationsPanel embedded />}
        {tab === "products" && <PresidentProductsTab />}
        {tab === "content" && <AdminContentTab />}
      </div>
    </div>
  );
}

function PresidentProductsTab() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  return <ProductsTab products={products} t={t} reload={load} />;
}


function Stat({ label, value, icon: Icon, accent, onClick }) {
  const base = `p-6 text-left w-full ${accent || "bg-white border border-slate-200"}`;
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <Icon className="w-6 h-6 mb-3 opacity-80" strokeWidth={1.5} />
        {onClick && <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />}
      </div>
      <div className="font-serif text-4xl">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] mt-2 opacity-70">{label}</div>
    </>
  );
  if (!onClick) return <div className={base} data-testid={`stat-${label}`}>{inner}</div>;
  return (
    <button type="button" onClick={onClick} title={`Ouvrir : ${label}`}
      className={`${base} group cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-club-yellow`}
      data-testid={`stat-${label}`}>
      {inner}
    </button>
  );
}

function Overview({ data, onNav }) {
  if (!data) return <div className="text-slate-500">{tr("Chargement…")}</div>;
  return (
    <div data-testid="president-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Stat label={tr("Membres")} value={data.members_total} icon={Users} accent="bg-navy text-white" onClick={() => onNav("pending")} />
        <Stat label={tr("Admissions en attente")} value={data.admissions_pending} icon={ClipboardCheck} accent="bg-club-red text-white" onClick={() => onNav("admissions")} />
        <Stat label={tr("Messages nouveaux")} value={data.messages_new} icon={MessageSquare} onClick={() => onNav("messages")} />
        <Stat label={tr("Stages inscrits")} value={data.stage_regs_confirmed} icon={Trophy} onClick={() => onNav("stages")} />
        <Stat label={tr("Commandes payées")} value={data.orders_paid} icon={Wallet} accent="bg-club-yellow text-navy" onClick={() => onNav("products")} />
        <Stat label={tr("Commandes en attente")} value={data.orders_pending} icon={Wallet} onClick={() => onNav("products")} />
        <Stat label={tr("Revenu")} value={`€${data.revenue_total.toFixed(0)}`} icon={Wallet} accent="bg-white border border-club-yellow" onClick={() => onNav("products")} />
        <Stat label={tr("Régates à venir")} value={data.upcoming_regattas.length} icon={CalendarDays} onClick={() => onNav("regattas")} />
      </div>

      <h3 className="font-serif text-2xl text-navy mt-12 mb-3">{tr("Revenus par catégorie")}</h3>
      <div className="divider-gold mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(data.revenue_by_category || {}).map(([cat, total]) => (
          <button type="button" key={cat} onClick={() => onNav("products")} title="Ouvrir : Boutique / Stages"
            className="bg-white border border-slate-200 p-4 text-left group cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md hover:border-club-yellow focus:outline-none focus:ring-2 focus:ring-club-yellow"
            data-testid={`rev-${cat}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-club-red font-semibold">{cat}</div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-club-red group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
            </div>
            <div className="font-serif text-2xl text-navy mt-2">€ {Number(total).toFixed(0)}</div>
          </button>
        ))}
        {Object.keys(data.revenue_by_category || {}).length === 0 && (
          <div className="col-span-3 text-slate-500 text-sm">{tr("Aucun paiement enregistré.")}</div>
        )}
      </div>
    </div>
  );
}

function AdmissionsView({ admissions, reload, lang }) {
  const [filter, setFilter] = useState("new");
  const filtered = filter === "all" ? admissions : admissions.filter((a) => a.status === filter);

  const decide = async (id, status, notes = "") => {
    const fd = new FormData();
    fd.append("status", status);
    if (notes) fd.append("decision_notes", notes);
    await api.patch(`/president/admissions/${id}`, fd);
    reload();
  };

  return (
    <div data-testid="president-admissions">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admissions-filter">
        {["new", "approved", "rejected", "contacted", "all"].map((s) => (
          <button
            key={s} onClick={() => setFilter(s)}
            className={`px-3 py-2 text-[11px] uppercase tracking-[0.2em] ${
              filter === s ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"
            }`}
            data-testid={`admissions-filter-${s}`}
          >
            {s} ({s === "all" ? admissions.length : admissions.filter((a) => a.status === s).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">{tr("Aucune demande.")}</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 p-5" data-testid={`admission-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-serif text-xl text-navy">{a.first_name} {a.last_name}</div>
                  <div className="text-sm text-slate-500">{a.email} · {a.phone}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {new Date(a.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)} ·
                    Type: <span className="font-semibold">{a.membership_type}</span>
                  </div>
                  {a.motivation && <p className="text-sm text-slate-600 mt-2 italic">«&nbsp;{a.motivation}&nbsp;»</p>}
                  {a.decision_notes && (
                    <p className="text-xs text-slate-500 mt-2">Décision : {a.decision_notes} ({a.decided_by})</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                    a.status === "approved" ? "bg-green-100 text-green-800" :
                    a.status === "rejected" ? "bg-red-100 text-red-800" :
                    a.status === "contacted" ? "bg-club-yellow/20 text-club-yellow-dark" :
                    "bg-slate-100 text-slate-700"
                  }`}>{a.status}</span>
                </div>
              </div>
              {a.status !== "approved" && a.status !== "rejected" && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => decide(a.id, "approved")} className="inline-flex items-center gap-1.5 bg-green-700 text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-green-800" data-testid={`approve-${a.id}`}>
                    <Check className="w-3.5 h-3.5" />{tr("Approuver")}</button>
                  <button onClick={() => decide(a.id, "rejected")} className="inline-flex items-center gap-1.5 bg-club-red text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid={`reject-${a.id}`}>
                    <X className="w-3.5 h-3.5" />{tr("Refuser")}</button>
                  <button onClick={() => decide(a.id, "contacted")} className="inline-flex items-center gap-1.5 bg-navy text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-club-red" data-testid={`contact-${a.id}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Contacter
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesView({ messages, lang }) {
  if (messages.length === 0) {
    return <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">{tr("Aucun message.")}</div>;
  }
  return (
    <div className="space-y-3" data-testid="president-messages">
      {messages.map((m) => (
        <div key={m.id} className="bg-white border border-slate-200 p-5" data-testid={`message-${m.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1">
              <div className="font-serif text-lg text-navy">{m.subject}</div>
              <div className="text-xs text-slate-500">{m.name} — {m.email} · {new Date(m.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</div>
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{m.message}</p>
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-slate-100 text-slate-700">{m.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AgendaView({ data, lang }) {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="president-agenda">
      <div>
        <h3 className="font-serif text-2xl text-navy mb-3">{tr("Régates à venir")}</h3>
        <div className="divider-gold mb-4" />
        {data.upcoming_regattas.length === 0 ? (
          <div className="text-slate-500 text-sm">{tr("Aucune régate prévue.")}</div>
        ) : data.upcoming_regattas.map((r) => (
          <div key={r.slug} className="bg-white border border-slate-200 p-4 mb-3 flex justify-between items-center" data-testid={`pres-regatta-${r.slug}`}>
            <div>
              <div className="font-serif text-lg text-navy">{r.name}</div>
              <div className="text-xs text-slate-500">{new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-serif text-2xl text-navy mb-3">{tr("Événements à venir")}</h3>
        <div className="divider-gold mb-4" />
        {data.upcoming_events.length === 0 ? (
          <div className="text-slate-500 text-sm">{tr("Aucun événement prévu.")}</div>
        ) : data.upcoming_events.map((e) => (
          <div key={e.id} className="bg-white border border-slate-200 p-4 mb-3" data-testid={`pres-event-${e.id}`}>
            <div className="font-serif text-lg text-navy">{e.title_fr}</div>
            <div className="text-xs text-slate-500">{new Date(e.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)} · {e.location}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingUsersView({ users, reload, lang }) {
  const setStatus = async (id, status) => {
    await api.patch(`/admin/users/${id}/status`, { status });
    reload();
  };

  if (!users || users.length === 0) {
    return <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="pending-empty">{tr("Aucune inscription en attente.")}</div>;
  }
  return (
    <div className="space-y-3" data-testid="president-pending">
      <p className="text-sm text-slate-500 mb-4">
        Ces personnes se sont inscrites via le formulaire d'inscription. Approuvez pour leur permettre de se connecter.
      </p>
      {users.map((u) => (
        <div key={u.id} className="bg-white border border-slate-200 p-5" data-testid={`pending-${u.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl text-navy">{u.name}</div>
              <div className="text-sm text-slate-500">{u.email}</div>
              <div className="text-xs text-slate-400 mt-1">
                {tr("Inscrit le")} {new Date(u.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setStatus(u.id, "active")} className="inline-flex items-center gap-1.5 bg-green-700 text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-green-800" data-testid={`pending-approve-${u.id}`}>
                <Check className="w-3.5 h-3.5" />{tr("Activer")}</button>
              <button onClick={() => setStatus(u.id, "rejected")} className="inline-flex items-center gap-1.5 bg-club-red text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid={`pending-reject-${u.id}`}>
                <X className="w-3.5 h-3.5" />{tr("Refuser")}</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
